import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosConfig from "../util/axiosConfig";
import { API_ENDPOINTS } from "../util/apiEndpoints";
import { AppContext } from "../context/AppContext";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    IconButton,
    InputAdornment,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";

const Login = ({ open, onClose }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { setUser } = useContext(AppContext);
    const navigate = useNavigate();

    // password visibility state
    const [showPassword, setShowPassword] = useState(false);
    const toggleShowPassword = () => setShowPassword((s) => !s);

    useEffect(() => {
        if (open) {
            setUsername("");
            setPassword("");
            setError(null);
            setShowPassword(false);
        }
    }, [open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (username.trim()) {
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!regex.test(username)) {
                setError("Please enter a valid email address");
                setIsLoading(false);
                return;
            }
        }

        if (!password.trim()) {
            setError("Password cannot be empty");
            setIsLoading(false);
            return;
        }

        try {
            const response = await axiosConfig.post(API_ENDPOINTS.LOGIN, {
                username,
                password,
            });
            console.log("Full response.data:", response.data);

            const { token, employeeId, role, expiresIn } = response.data;

            if (token) {
                localStorage.setItem("token", token);
                const loggedInUser = {
                    employeeId,
                    role,
                    expiresIn,
                };
                setUser(loggedInUser);
                setError(null);
                toast.success("Login successful!");
                navigate("/");
                onClose();
            }
        } catch (error) {
            console.error("Login error:", error);
            setError(
                error?.response?.data?.message || "An error occurred during login. Please try again."
            );
        } finally {
            setIsLoading(false);
            setUsername("");
            setPassword("");
            setShowPassword(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: (theme) => ({
                    borderTop: `5px solid ${theme.palette.primary.dark}`,
                    backgroundColor: theme.palette.dialogBackground,
                    width: 400,
                    maxWidth: "none",
                    padding: 3,
                }),
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "text.secondary",
                }}
                size="small"
                aria-label="close"
            >
                <CloseIcon />
            </IconButton>

            <DialogTitle sx={{ fontWeight: 700, fontSize: "1.3rem", padding: 0, marginBottom: 1 }}>
                Login
            </DialogTitle>
            <Typography sx={{ color: "text.secondary" }}>Enter your credentials to access your account.</Typography>

            {/* Wrap inputs in a form so Enter key works */}
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="username"
                            fullWidth
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="name@example.com"
                            autoComplete="username"
                        />

                        <TextField
                            label="Password"
                            fullWidth
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            onClick={toggleShowPassword}
                                            edge="end"
                                            size="medium"
                                        >
                                            {showPassword ? <VisibilityOff sx={{ color: "primary.main" }} /> : <Visibility sx={{ color: "primary.main" }} />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                    <Button
                        endIcon={isLoading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                        variant="contained"
                        type="submit"
                        disabled={isLoading}
                        sx={{ marginBottom: 2 }}
                    >
                        {isLoading ? "LOGGING IN..." : "LOGIN"}
                    </Button>
                </DialogActions>
            </form>

            {error && (
                <Typography sx={{ color: "error.main", fontSize: "1rem", marginBottom: 1 }}>{error}*</Typography>
            )}

            {/* <Typography sx={{ color: "text.secondary" }}>
                Forgot password?{" "}
                <Typography
                    component="span"
                    sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Reset here
                </Typography>
            </Typography> */}
        </Dialog>
    );
};

export default Login;
